/**
 * A selection as shared with Scout: IDs and a count, never names.
 * A `type`, not an `interface`, so it is assignable to the
 * `Record<string, unknown>` that `useScoutContext` accepts.
 */
export type ScoutSelection = {
  count: number;
  provider_ids: string[];
};

/**
 * Turn selected provider IDs into the value passed to `useScoutContext`, or
 * null when nothing is selected. Missing IDs are dropped and the rest are
 * stringified. `total` overrides the count when only some IDs are loaded
 * (e.g. "select all" over infinite scroll).
 */
export default function toScoutSelection(
  ids: readonly unknown[] | null | undefined,
  total?: number | null
): ScoutSelection | null {
  const providerIds = (ids ?? [])
    .map((id) =>
      typeof id === 'string' || typeof id === 'number' ? String(id) : ''
    )
    .filter(Boolean);
  if (providerIds.length === 0) return null;
  return {
    count: Math.max(total ?? 0, providerIds.length),
    provider_ids: providerIds,
  };
}

/**
 * The provider open in a drawer, as shared with Scout: ID and type only.
 * `provider_id` repeats the ID as a scalar so the `On screen:` line names
 * which ID is "this provider"; `provider_ids` puts it in the tool arguments.
 * A `type` for the same reason as `ScoutSelection`.
 */
export type ScoutOpenProvider = {
  provider_ids: [string];
  provider_id: string;
  type?: string;
};

/**
 * Turn the provider open in a drawer into the value passed to
 * `useScoutContext`, or null while the drawer is closed or empty.
 */
export function toScoutOpenProvider(
  isOpen: boolean,
  providerId: string | number | null | undefined,
  providerType?: string | null
): ScoutOpenProvider | null {
  if (!isOpen || providerId == null || providerId === '') return null;
  const id = String(providerId);
  return {
    provider_ids: [id],
    provider_id: id,
    ...(providerType ? { type: providerType } : {}),
  };
}
